import { EmptyState } from '@atlas/ui-components'
import { AssetGridSkeleton } from '@atlas/ui-domain'
import type { FileWithTagsDTO } from '@/services/file.service'
import { AssetCard } from './AssetCard'

interface AssetGridProps {
	files: FileWithTagsDTO[]
	/** Presigned thumbnail URLs keyed by fileId (null where unavailable). */
	thumbnails: Record<string, string | null>
	isLoading?: boolean
	/** Opens a card's asset detail page, given its fileId. */
	onOpenAsset?: (fileId: string) => void
	/** Asks to delete a card's asset. Omitted where deletion isn't offered. */
	onDeleteAsset?: (file: FileWithTagsDTO) => void
	/** Whether the grid is selecting: its cards are toggles rather than links. */
	selectionMode?: boolean
	/** The fileIds currently selected. Meaningless without `onToggleSelect`. */
	selectedFileIds?: ReadonlySet<string>
	/** Adds or removes a card from the selection. Omitted where selection isn't offered. */
	onToggleSelect?: (fileId: string) => void
	/**
	 * Whether a card's delete control is offered. Omitting it withholds delete
	 * from every card: a caller has to say who may, because a permission question
	 * nobody asked must not answer itself as yes.
	 */
	canDeleteAsset?: (file: FileWithTagsDTO) => boolean
}

/** Responsive asset-card grid. Shows an empty state when no files match the filter. */
export function AssetGrid({
	files,
	thumbnails,
	isLoading,
	onOpenAsset,
	onDeleteAsset,
	selectionMode,
	selectedFileIds,
	onToggleSelect,
	canDeleteAsset,
}: AssetGridProps) {
	if (isLoading) {
		return <AssetGridSkeleton label="Loading assets…" />
	}
	if (files.length === 0) {
		return <EmptyState title="No files match the current filters." variant="filtered" />
	}
	return (
		<div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 h-fit">
			{files.map((file) => (
				<AssetCard
					key={file.fileId}
					file={file}
					thumbnailUrl={thumbnails[file.fileId] ?? null}
					onOpen={onOpenAsset ? () => onOpenAsset(file.fileId) : undefined}
					onDelete={onDeleteAsset ? () => onDeleteAsset(file) : undefined}
					selectionMode={selectionMode}
					selected={selectedFileIds?.has(file.fileId) ?? false}
					onToggleSelect={onToggleSelect ? () => onToggleSelect(file.fileId) : undefined}
					canDelete={canDeleteAsset?.(file) ?? false}
				/>
			))}
		</div>
	)
}
